import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Flex, Image } from 'components/ui';
import { space } from 'style/config';
import Chiclet from 'components/Chiclet';
import { wrapFunctionalComponent } from 'utils/framework';
import { productSelector } from 'selectors/page/product/productSelector';
import productActions from 'actions/ProductActions';
import UI from 'utils/UI';
import localeUtils from 'utils/LanguageLocale';
import { SENTIMENT_POSI_ICON, SENTIMENT_NEGI_ICON } from 'ai/utils/imagePaths';
import { SENTIMENT_TYPES } from 'ai/constants/superchat';
import { trackAspectChipClick, trackAspectChipReviewFilter } from 'ai/analytics/trackers/prsTrackers';

const NUMBER_OF_PROS_AND_CONS = 3;
const LIMIT_OF_REVIEWS = 6;

function AspectChips() {
    const product = useSelector(productSelector);
    const dispatch = useDispatch();
    const sentiments = product?.sentiments;
    const selectedSentiment = product?.selectedSentiment;
    const selectedType = product?.highlightedReviews?.proConType;

    if (!sentiments || !sentiments.length) {
        return null;
    }

    const pros = sentiments.filter(s => s.type === SENTIMENT_TYPES.PROS);
    const cons = sentiments.filter(s => s.type === SENTIMENT_TYPES.CONS);

    const handleChicletClick = (sentiment, count, type) => {
        const productId = product?.productDetails?.productId;

        if (selectedSentiment !== sentiment || selectedType !== type) {
            dispatch(
                productActions.loadHighlightedReviews({
                    sentiment,
                    type,
                    productId,
                    limit: LIMIT_OF_REVIEWS,
                    page: 0,
                    language: localeUtils.getCurrentLanguage(),
                    sentimentSource: 'seph_sentiment'
                })
            );
            trackAspectChipClick({ aspectName: sentiment, count, type, productId });
            trackAspectChipReviewFilter({ aspectName: sentiment, type });
            UI.scrollTo({ elementId: 'ratings-reviews-container' });
        } else {
            dispatch(productActions.setSelectedSentiment(''));
            dispatch(productActions.removeHighlightedReviews());
        }
    };

    const renderChips = (items, type, iconSrc) => {
        return items.slice(0, NUMBER_OF_PROS_AND_CONS).map(item => (
            <Box
                key={item.sentiment}
                css={styles.chipWrapper}
            >
                <Chiclet
                    variant='shadow'
                    fontSize='sm'
                    customPaddingX={2}
                    isActive={item.sentiment === selectedSentiment && type === selectedType}
                    onClick={() => handleChicletClick(item.sentiment, item.count, type)}
                >
                    <Flex
                        alignItems='center'
                        gap={1}
                    >
                        <Image
                            src={iconSrc}
                            size={16}
                        />
                        <span>{`${item.sentiment} (${item.count.toLocaleString()})`}</span>
                    </Flex>
                </Chiclet>
            </Box>
        ));
    };

    return (
        <Flex
            flexDirection='column'
            gap={3}
            data-at={Sephora.debug.dataAt('prs_aspect_chips')}
        >
            {pros.length > 0 && (
                <Flex
                    flexWrap='nowrap'
                    gap={2}
                    overflowX='auto'
                    css={styles.scrollContainer}
                >
                    {renderChips(pros, SENTIMENT_TYPES.PROS, SENTIMENT_POSI_ICON)}
                </Flex>
            )}
            {cons.length > 0 && (
                <Flex
                    flexWrap='nowrap'
                    gap={2}
                    overflowX='auto'
                    css={styles.scrollContainer}
                >
                    {renderChips(cons, SENTIMENT_TYPES.CONS, SENTIMENT_NEGI_ICON)}
                </Flex>
            )}
        </Flex>
    );
}

const styles = {
    scrollContainer: {
        padding: space[2],
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
            display: 'none'
        }
    },
    chipWrapper: {
        flexShrink: 0
    }
};

export default wrapFunctionalComponent(AspectChips, 'AspectChips');
